import { toastr } from 'react-redux-toastr';
import { I18n } from 'src/translations/i18n';

import FileDownload from 'js-file-download';

import {
  takeLatest, put, select, call
} from 'redux-saga/effects';

import parseMainReport from 'src/containers/Reports/ConfirmWorkHoursReport/confirmWorkHoursReportParser';
import {
  confirmWorkhoursActions,
  selectConfirmWorkhoursCacheId
} from 'src/store/reducers/confirmWorkHoursReportReducer';

import { selectMaxConfirmLevel } from 'src/store/selectors/confirmatorsAdministration';

import { loadExcelRequest, loadExcelFail, loadExcelSuccess } from 'src/store/actions/excelActions';
import { selectUserId } from 'src/store/reducers/userInfoReducer';
import toastrError from 'src/helpers/toastr/toastrError';

import toastrSuccess from 'src/helpers/toastr/toastrSuccess';
import { wtsApi } from 'src/api';
import { isEmpty, isNil } from 'ramda';

function* watchConfirmWorkHours() {
  yield [
    takeLatest(confirmWorkhoursActions.report.request, confirmWorkHoursReportSaga),
    takeLatest(confirmWorkhoursActions.confirm.request, confirmWorkHoursSaga),
    takeLatest(confirmWorkhoursActions.confirmLevels.request, confirmLevelsSaga),
    takeLatest(confirmWorkhoursActions.report.requestCsv, confirmWorkHoursReportCsvSaga),
    takeLatest(confirmWorkhoursActions.report.requestCsv2, confirmWorkHoursReportCsv2Saga),
    takeLatest(confirmWorkhoursActions.report.requestDirecto, confirmWorkHoursReportDirectoSaga),
    takeLatest(confirmWorkhoursActions.report.requestAxapta, confirmWorkHoursReportAxaptaSaga),
    takeLatest(confirmWorkhoursActions.report.requestPersonaCsv, confirmWorkHoursReportPersonaCsvSaga),
    takeLatest(confirmWorkhoursActions.report.requestTaaviCsv, confirmWorkHoursReportTaaviCsvSaga),
    takeLatest(confirmWorkhoursActions.report.requestExcel, confirmWorkHoursReportExcelSaga),
    takeLatest(confirmWorkhoursActions.report.requestExcelV2, confirmWorkHoursReportExcelV2Saga),
    takeLatest(confirmWorkhoursActions.summedOverhours.request, confirmWorkHoursSummedOverhoursSaga)
  ];
}

function* confirmWorkHoursReportSaga({ payload }) {
  try {
    const response = yield call(wtsApi.reports.mainReportList, payload);
    const data = response.data.Data;
    if (isNil(data) || isEmpty(data)) {
      toastr.error(I18n.t('NoDataAvailable'));
    }
    const confirmLevels = yield select((state) => selectMaxConfirmLevel(state, 'MainReport'));
    if (data) {
      const workDaysResponse = yield call(wtsApi.workDays.workDaysList, { period: payload.period });
      const workDays = workDaysResponse.data.Data;
      const {
        Period: period,
        Departments: departments,
        CacheId: cacheId
      } = data;
      const report = parseMainReport(confirmLevels, departments);
      const reportWithContracts = report.map(({ jobsByUsers, ...other }) => ({
        ...other,
        jobsByUsers
      }));
      if (!report.length) {
        toastr.warning(I18n.t('NoDataAvailable'));
      }
      yield put({
        type: confirmWorkhoursActions.report.success,
        payload: {
          period,
          report: reportWithContracts,
          cacheId,
          workDays: workDays.WorkDays,
          totalWorkingTime: workDays.TotalWorkingTime,
          params: payload
        }
      });
    }
  } catch (e) {
    toastrError(e);
    yield put({
      type: confirmWorkhoursActions.report.fail
    });
  }
}

function* confirmWorkHoursSaga({ payload }) {
  try {
    yield put({ type: confirmWorkhoursActions.confirm.loading, payload: true });
    const {
      option,
      userIds
    } = payload;
    const cacheId = yield select(selectConfirmWorkhoursCacheId);
    const currentUserId = yield select(selectUserId);
    const params = {
      ids: (userIds || []).join(','),
      action: option,
      reportId: 'MainReport',
      cacheId,
      confirmerUserId: currentUserId
    };
    const response = yield call(wtsApi.confirmationDatas.fromCacheCreate, params);
    const resp = response.data.Data;
    const {
      Period: period,
      Departments: departments,
      CacheId
    } = resp;
    const confirmLevels = yield select((state) => selectMaxConfirmLevel(state, 'MainReport'));
    const report = parseMainReport(confirmLevels, departments);
    yield put({
      type: confirmWorkhoursActions.report.success,
      payload: {
        period,
        report,
        cacheId: CacheId,
        params: payload
      }
    });
  } catch (e) {
    yield put({ type: confirmWorkhoursActions.confirm.loading, payload: false });
    toastrError(e);
  }
}

function* confirmLevelsSaga() {
  try {
    const confirmLevelsResponse = yield call(wtsApi.configurations.confirmationLevelsList);
    const confirmLevels = confirmLevelsResponse.data.Data.Levels;
    if (!confirmLevels) {
      toastr.warning(I18n.t('ApproversLevelsIsMissing'));
    }
    yield put({
      type: confirmWorkhoursActions.confirmLevels.success,
      payload: confirmLevels
    });
  } catch (e) {
    toastrError(e);
  }
}

function* confirmWorkHoursReportCsvSaga({ payload: cacheid }) {
  try {
    const response = yield call(wtsApi.reports.mainReportCsvExportList, { cacheid }, { format: 'blob' });
    const fileName = response.headers['x-suggested-filename'] || 'Raport.xlsx';
    FileDownload(response.data, fileName);
  } catch (e) {
    toastrError(e);
  }
}

function* confirmWorkHoursReportCsv2Saga({ payload: cacheid }) {
  try {
    const response = yield call(wtsApi.reports.mainReportCsvExport2List, { cacheid }, { format: 'blob' });
    const fileName = response.headers['x-suggested-filename'] || 'Raport.xlsx';
    FileDownload(response.data, fileName);
  } catch (e) {
    toastrError(e);
  }
}

function* confirmWorkHoursReportDirectoSaga({ payload: cacheid }) {
  try {
    const response = yield call(wtsApi.reports.mainReportDirectoExcelExportList, { cacheid }, { format: 'blob' });
    const fileName = response.headers['x-suggested-filename'] || 'Raport.xlsx';
    FileDownload(response.data, fileName);
  } catch (e) {
    toastrError(e);
  }
}

function* confirmWorkHoursReportAxaptaSaga({ payload }) {
  try {
    const response = yield call(wtsApi.exports.exportWorkHoursToAxaptaCreate, payload);
    toastrSuccess(response);
  } catch (e) {
    toastrError(e);
  }
}

function* confirmWorkHoursReportPersonaCsvSaga({ payload: cacheid }) {
  try {
    const response = yield call(wtsApi.reports.mainReportPersonaCsvExportList, { cacheid }, { format: 'blob' });
    const fileName = response.headers['x-suggested-filename'] || 'Raport.xlsx';
    FileDownload(response.data, fileName);
  } catch (e) {
    toastrError(e);
  }
}

function* confirmWorkHoursReportTaaviCsvSaga({ payload: cacheid }) {
  try {
    const response = yield call(wtsApi.reports.mainReportTaaviCsvExportList, { cacheid }, { format: 'blob' });
    const fileName = response.headers['x-suggested-filename'] || 'Raport.xlsx';
    FileDownload(response.data, fileName);
  } catch (e) {
    toastrError(e);
  }
}

function* confirmWorkHoursReportExcelSaga({ payload: responseData }) {
  try {
    yield put(loadExcelRequest());
    const response = yield call(wtsApi.reports.mainReportExcelExportList, responseData, { format: 'blob' });
    const fileName = response.headers['x-suggested-filename'] || 'Raport.xlsx';
    yield put(loadExcelSuccess());
    FileDownload(response.data, fileName);
  } catch (e) {
    toastrError(e);
    yield put(loadExcelFail());
  }
}

function* confirmWorkHoursReportExcelV2Saga({ payload: responseData }) {
  try {
    yield put(loadExcelRequest());
    const response = yield call(wtsApi.reports.mainReportExcelExportV2List, responseData, { format: 'blob' });
    const fileName = response.headers['x-suggested-filename'] || 'Raport.xlsx';
    yield put(loadExcelSuccess());
    FileDownload(response.data, fileName);
  } catch (e) {
    toastrError(e);
    yield put(loadExcelFail());
  }
}

function* confirmWorkHoursSummedOverhoursSaga({ payload: cacheId }) {
  try {
    yield put({ type: confirmWorkhoursActions.summedOverhours.loading });
    const response = yield call(wtsApi.reports.overHoursFromMainReportList, { cacheId });
    yield put({
      type: confirmWorkhoursActions.summedOverhours.success,
      payload: response.data.Data || []
    });
  } catch (e) {
    toastrError(e);
    yield put({ type: confirmWorkhoursActions.summedOverhours.fail });
  }
}

export default watchConfirmWorkHours;
